import React from 'react';
import { visitorMediaUrl, visitorMediaFallbackUrl } from '../utils/visitorMedia';
import './VisitorDetailsModal.css';

const VisitorDetailsModal = ({ visitor, onClose, loading }) => {
  if (!visitor && !loading) return null;

  // Debug: Log visitor data to check image fields
  if (visitor) {
    console.log('Visitor Details:', {
      id: visitor.id,
      name: visitor.name || visitor.vname,
      profile_pic: visitor.profile_pic,
      aadhar_pic: visitor.aadhar_pic
    });
  }

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const getStatusLabel = (status) => {
    switch (String(status)) {
      case '0': return 'Pending';
      case '1': return 'Active';
      case '2': return 'Inactive';
      default: return 'Unknown';
    }
  };

  const getGenderLabel = (gender) => {
    if (!gender || gender === 'N/A') return 'Not specified';
    return gender;
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content visitor-details-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Visitor Details</h3>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        {loading ? (
          <div className="modal-body">
            <div className="loading">Loading visitor details...</div>
          </div>
        ) : visitor ? (
          <div className="modal-body">
            <div className="details-grid">
              <div className="detail-group">
                <label>ID</label>
                <div className="detail-value">{visitor.id || visitor.vid}</div>
              </div>

              {visitor.token && (
                <div className="detail-group">
                  <label>Token Number</label>
                  <div className="detail-value token-number">{visitor.token}</div>
                </div>
              )}

              <div className="detail-group">
                <label>Name</label>
                <div className="detail-value">{visitor.vname || visitor.name || 'N/A'}</div>
              </div>

              <div className="detail-group">
                <label>Phone</label>
                <div className="detail-value">{visitor.vphone || visitor.mobile || 'N/A'}</div>
              </div>

              <div className="detail-group">
                <label>Category</label>
                <div className="detail-value">{visitor.category || 'N/A'}</div>
              </div>

              <div className="detail-group">
                <label>Registration Date</label>
                <div className="detail-value">{formatDate(visitor.ad_date || visitor.reg_date)}</div>
              </div>

              {visitor.firm && (
                <div className="detail-group full-width">
                  <label>Firm/Company</label>
                  <div className="detail-value">{visitor.firm}</div>
                </div>
              )}

              {(visitor.aadhar || visitor.adhar) && (
                <div className="detail-group full-width">
                  <label>Aadhar Number</label>
                  <div className="detail-value">{visitor.aadhar || visitor.adhar}</div>
                </div>
              )}

              <div className="detail-group">
                <label>State</label>
                <div className="detail-value">{visitor.state || 'N/A'}</div>
              </div>

              <div className="detail-group">
                <label>City</label>
                <div className="detail-value">{visitor.city || 'N/A'}</div>
              </div>

              {(visitor.gst || visitor.pin) && (
                <div className="detail-group">
                  <label>GST</label>
                  <div className="detail-value">{visitor.gst || visitor.pin}</div>
                </div>
              )}

              {visitor.bnature && (
                <div className="detail-group">
                  <label>Business Nature</label>
                  <div className="detail-value">{visitor.bnature}</div>
                </div>
              )}

              {visitor.profile_pic && (
                <div className="detail-group full-width">
                  <label>Photo</label>
                  <div className="detail-value">
                    <img 
                      src={visitorMediaUrl('profile_image', visitor.profile_pic)} 
                      alt="Visitor Photo" 
                      style={{maxWidth: '200px', maxHeight: '200px', borderRadius: '8px', border: '2px solid #ddd', display: 'block', marginBottom: '10px'}}
                      onError={(e) => {
                        const fallback = visitorMediaFallbackUrl('profile_image', visitor.profile_pic);
                        if (fallback && e.target.src !== fallback) {
                          e.target.src = fallback;
                          return;
                        }
                        e.target.style.display = 'none';
                        e.target.parentElement.innerHTML = '<span style="color: #999;">Image not available</span>';
                      }}
                      onLoad={() => console.log('Photo loaded successfully')}
                    />
                    <a 
                      href={visitorMediaUrl('profile_image', visitor.profile_pic)}
                      download={`visitor_${visitor.id}_photo.png`}
                      className="btn btn-sm btn-primary"
                      style={{display: 'inline-block', padding: '5px 15px', marginRight: '10px', textDecoration: 'none', backgroundColor: '#00a86b', color: 'white', borderRadius: '4px'}}
                    >
                      <i className="fas fa-download"></i> Download Photo
                    </a>
                    <button
                      onClick={() => window.open(visitorMediaUrl('profile_image', visitor.profile_pic), '_blank')}
                      className="btn btn-sm btn-secondary"
                      style={{display: 'inline-block', padding: '5px 15px', backgroundColor: '#6c757d', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer'}}
                    >
                      <i className="fas fa-external-link-alt"></i> View Full Size
                    </button>
                  </div>
                </div>
              )}

              {visitor.aadhar_pic && (
                <div className="detail-group full-width">
                  <label>Aadhar Card</label>
                  <div className="detail-value">
                    <img 
                      src={visitorMediaUrl('aadhar', visitor.aadhar_pic)} 
                      alt="Aadhar Card" 
                      style={{maxWidth: '400px', maxHeight: '300px', borderRadius: '8px', border: '2px solid #ddd', cursor: 'pointer', display: 'block', marginBottom: '10px'}}
                      onClick={(e) => window.open(e.target.src, '_blank')}
                      onError={(e) => {
                        const fallback = visitorMediaFallbackUrl('aadhar', visitor.aadhar_pic);
                        if (fallback && e.target.src !== fallback) {
                          e.target.src = fallback;
                          return;
                        }
                        e.target.style.display = 'none';
                        e.target.parentElement.innerHTML = '<span style="color: #999;">Image not available</span>';
                      }}
                      onLoad={() => console.log('Aadhar loaded successfully')}
                      title="Click to view full size"
                    />
                    <a 
                      href={visitorMediaUrl('aadhar', visitor.aadhar_pic)}
                      download={`visitor_${visitor.id}_aadhar.png`}
                      className="btn btn-sm btn-primary"
                      style={{display: 'inline-block', padding: '5px 15px', marginRight: '10px', textDecoration: 'none', backgroundColor: '#00a86b', color: 'white', borderRadius: '4px'}}
                    >
                      <i className="fas fa-download"></i> Download Aadhar
                    </a>
                    <button
                      onClick={() => window.open(visitorMediaUrl('aadhar', visitor.aadhar_pic), '_blank')}
                      className="btn btn-sm btn-secondary"
                      style={{display: 'inline-block', padding: '5px 15px', backgroundColor: '#6c757d', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer'}}
                    >
                      <i className="fas fa-external-link-alt"></i> View Full Size
                    </button>
                  </div>
                </div>
              )}

              {visitor['qr-code'] && (
                <div className="detail-group full-width">
                  <label>QR Code</label>
                  <div className="detail-value">
                    <img 
                      src={`/generated-qr/${visitor['qr-code']}`} 
                      alt="QR Code" 
                      style={{maxWidth: '150px', borderRadius: '5px'}}
                    />
                  </div>
                </div>
              )}

              {visitor.checked && (
                <div className="detail-group">
                  <label>Terms Accepted</label>
                  <div className="detail-value">{visitor.checked === '1' ? 'Yes' : 'No'}</div>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="modal-body">
            <div className="error">Failed to load visitor details</div>
          </div>
        )}

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default VisitorDetailsModal;
